import { useEffect, useRef } from 'react';
import type { CalendarTarget, TimeOffEntry } from '../../../api/timeOff';

/**
 * Digest of per-target calendar sync state. The sync runner only mutates the calendar links
 * (clearing isDirty, stamping lastSyncResult / graphEventId / lastSyncedAt) without touching the
 * entry's rowVersion or status, so this must be part of the snapshot key — otherwise a completed
 * sync looks "unchanged" and the calendar keeps showing a stale "waiting to sync" banner.
 */
function timeOffEntryLinkSyncDigest(entry: TimeOffEntry): string {
  return (entry.calendarLinks ?? [])
    .map(
      (link) =>
        `${link.calendarTargetId}:${link.isDirty ? 1 : 0}:${link.lastSyncResult ?? ''}:${link.graphEventId ?? ''}:${link.lastSyncedAt ?? ''}`
    )
    .sort()
    .join('|');
}

export function timeOffEntrySnapshotKey(entry: TimeOffEntry): string {
  return `${entry.id}:${entry.rowVersion}:${entry.status}:${timeOffEntryLinkSyncDigest(entry)}`;
}

export function calendarEntriesUnchanged(prev: TimeOffEntry[], next: TimeOffEntry[]): boolean {
  if (prev.length !== next.length) return false;
  const prevKeys = prev.map(timeOffEntrySnapshotKey).sort();
  const nextKeys = next.map(timeOffEntrySnapshotKey).sort();
  return prevKeys.every((key, index) => key === nextKeys[index]);
}

function calendarTargetSnapshotKey(target: CalendarTarget): string {
  return `${target.id}:${target.isEnabled}:${target.displayName}:${target.syncDirection}`;
}

export function calendarTargetsUnchanged(prev: CalendarTarget[], next: CalendarTarget[]): boolean {
  if (prev.length !== next.length) return false;
  const prevKeys = prev.map(calendarTargetSnapshotKey).sort();
  const nextKeys = next.map(calendarTargetSnapshotKey).sort();
  return prevKeys.every((key, index) => key === nextKeys[index]);
}

import { TIME_OFF_CALENDAR_SIGNALR_FALLBACK_POLL_MS } from './timeOffCalendarRealtimeTypes';

export function calendarPollIntervalMs(isNarrow: boolean, signalRConnected = false): number {
  if (signalRConnected) return TIME_OFF_CALENDAR_SIGNALR_FALLBACK_POLL_MS;
  return isNarrow ? 120_000 : 60_000;
}

export function shouldPauseCalendarPoll(options: { documentHidden: boolean; popupOpen: boolean }): boolean {
  return options.documentHidden || options.popupOpen;
}

export function useTimeOffCalendarPoll(options: {
  refreshSilent: () => Promise<void>;
  isNarrow: boolean;
  popupOpen: boolean;
  signalRConnected?: boolean;
}): void {
  const { refreshSilent, isNarrow, popupOpen, signalRConnected = false } = options;

  useEffect(() => {
    if (shouldPauseCalendarPoll({ documentHidden: document.hidden, popupOpen })) {
      return;
    }

    const intervalMs = calendarPollIntervalMs(isNarrow, signalRConnected);
    const id = window.setInterval(() => {
      if (shouldPauseCalendarPoll({ documentHidden: document.hidden, popupOpen })) {
        return;
      }
      void refreshSilent();
    }, intervalMs);

    return () => clearInterval(id);
  }, [refreshSilent, isNarrow, popupOpen, signalRConnected]);

  const wasPopupOpenRef = useRef(popupOpen);
  useEffect(() => {
    if (wasPopupOpenRef.current && !popupOpen) {
      void refreshSilent();
    }
    wasPopupOpenRef.current = popupOpen;
  }, [popupOpen, refreshSilent]);

  useEffect(() => {
    function catchUpOnReturn() {
      if (document.hidden) return;
      void refreshSilent();
    }

    document.addEventListener('visibilitychange', catchUpOnReturn);
    window.addEventListener('focus', catchUpOnReturn);
    return () => {
      document.removeEventListener('visibilitychange', catchUpOnReturn);
      window.removeEventListener('focus', catchUpOnReturn);
    };
  }, [refreshSilent]);
}
